/**
 * Mascot Configuration - Module-specific settings and pre-canned queries
 *
 * Each module has:
 * - Name and description
 * - Primary color scheme
 * - Context-aware keywords for color changing
 * - Pre-canned queries specific to that module
 */

export interface PreCannedQuery {
  label: string;
  text: string;
  description: string;
  icon: string;
}

export interface MascotConfig {
  name: string;
  primaryColor: string;
  secondaryColor: string;
  keywords: string[];
  queries: PreCannedQuery[];
}

const moduleConfigs: Record<string, MascotConfig> = {
  dashboard: {
    name: 'Dashboard Overview',
    primaryColor: '#4A32FF',
    secondaryColor: '#2567FF',
    keywords: ['dashboard', 'overview', 'summary', 'all modules'],
    queries: [
      {
        label: 'Show Financial Summary',
        text: 'Show me an overview of all financial metrics',
        description: 'Get high-level view of all modules',
        icon: '📊',
      },
      {
        label: 'What Can You Do?',
        text: 'What optimization models are available?',
        description: 'Learn about platform capabilities',
        icon: '🤔',
      },
      {
        label: 'Recent Results',
        text: 'Show me recent optimization results',
        description: 'View latest analysis outputs',
        icon: '📈',
      },
      {
        label: 'Quick Actions',
        text: 'What quick actions can I perform?',
        description: 'See available shortcuts',
        icon: '⚡',
      },
    ],
  },

  cashflow: {
    name: 'Cash Flow Optimization',
    primaryColor: '#14B8A6',
    secondaryColor: '#0D9488',
    keywords: ['cash', 'liquidity', 'treasury', 'inflow', 'outflow'],
    queries: [
      {
        label: 'Optimize Cash Flow',
        text: 'Optimize cash flow for the next 90 days',
        description: 'Run linear programming optimization',
        icon: '💰',
      },
      {
        label: 'Liquidity Forecast',
        text: 'What is my liquidity forecast?',
        description: 'View projected cash positions',
        icon: '📊',
      },
      {
        label: 'Investment Recommendations',
        text: 'What are the best investment options?',
        description: 'Get investment allocation suggestions',
        icon: '📈',
      },
      {
        label: 'Borrowing Strategy',
        text: 'Should I borrow or use cash reserves?',
        description: 'Compare borrowing vs cash usage',
        icon: '🏦',
      },
      {
        label: 'Working Capital',
        text: 'How can I improve working capital?',
        description: 'Get working capital optimization tips',
        icon: '⚙️',
      },
    ],
  },

  payables: {
    name: 'Payables Optimization',
    primaryColor: '#8B5CF6',
    secondaryColor: '#7C3AED',
    keywords: ['payables', 'payment', 'discount', 'dpo', 'vendor', 'invoice'],
    queries: [
      {
        label: 'Optimize Payment Schedule',
        text: 'Optimize my payment schedule to maximize discounts',
        description: 'Run Dynamic Programming optimization',
        icon: '📅',
      },
      {
        label: 'Discount Opportunities',
        text: 'What early payment discounts are available?',
        description: 'View 2/10 net 30 opportunities',
        icon: '💰',
      },
      {
        label: 'DPO Analysis',
        text: 'What is my current Days Payable Outstanding?',
        description: 'View DPO metrics and trends',
        icon: '📊',
      },
      {
        label: 'Working Capital Impact',
        text: 'How does payment timing affect working capital?',
        description: 'Analyze cash flow impact',
        icon: '💵',
      },
      {
        label: 'Vendor Prioritization',
        text: 'Which vendors should I pay first?',
        description: 'Get payment priority recommendations',
        icon: '🎯',
      },
    ],
  },

  inventory: {
    name: 'Inventory Optimization',
    primaryColor: '#F59E0B',
    secondaryColor: '#D97706',
    keywords: ['inventory', 'stock', 'sku', 'eoq', 'reorder', 'warehouse'],
    queries: [
      {
        label: 'Optimize Inventory Levels',
        text: 'Optimize inventory levels across all SKUs',
        description: 'Run EOQ and (Q,R) policy optimization',
        icon: '📦',
      },
      {
        label: 'Reorder Points',
        text: 'What are optimal reorder points?',
        description: 'View recommended reorder quantities',
        icon: '🔄',
      },
      {
        label: 'Safety Stock',
        text: 'Calculate safety stock requirements',
        description: 'Balance service level and costs',
        icon: '🛡️',
      },
      {
        label: 'ABC Classification',
        text: 'Show ABC classification of inventory',
        description: 'Identify high-value items',
        icon: '🏆',
      },
      {
        label: 'Carrying Costs',
        text: 'What are my total carrying costs?',
        description: 'View inventory holding expenses',
        icon: '💸',
      },
    ],
  },

  debt: {
    name: 'Debt Restructuring',
    primaryColor: '#EF4444',
    secondaryColor: '#DC2626',
    keywords: ['debt', 'refinancing', 'covenant', 'leverage', 'interest'],
    queries: [
      {
        label: 'Optimize Debt Structure',
        text: 'Optimize my debt portfolio and maturity profile',
        description: 'Run MILP optimization',
        icon: '🏦',
      },
      {
        label: 'Refinancing Opportunities',
        text: 'What refinancing opportunities exist?',
        description: 'Identify cost-saving refinancing options',
        icon: '🔄',
      },
      {
        label: 'Covenant Compliance',
        text: 'Check all debt covenant compliance',
        description: 'Verify covenant requirements',
        icon: '✅',
      },
      {
        label: 'Interest Rate Impact',
        text: 'How do rate changes affect my debt service?',
        description: 'Scenario analysis on rates',
        icon: '📊',
      },
      {
        label: 'Maturity Profile',
        text: 'Show debt maturity schedule',
        description: 'View upcoming maturities',
        icon: '📅',
      },
    ],
  },

  expenses: {
    name: 'Expense Anomaly Detection',
    primaryColor: '#EC4899',
    secondaryColor: '#DB2777',
    keywords: ['expense', 'anomaly', 'fraud', 'transaction', 'suspicious'],
    queries: [
      {
        label: 'Detect Anomalies',
        text: 'Scan for anomalous expense transactions',
        description: 'Run ensemble ML detection',
        icon: '🔍',
      },
      {
        label: 'High-Risk Transactions',
        text: 'Show high-risk expense transactions',
        description: 'View flagged items for review',
        icon: '⚠️',
      },
      {
        label: 'Spending Patterns',
        text: 'Analyze spending patterns by category',
        description: 'Identify unusual patterns',
        icon: '📊',
      },
      {
        label: 'Fraud Score',
        text: 'What is the fraud risk score?',
        description: 'View overall risk assessment',
        icon: '🎯',
      },
      {
        label: 'Investigation Queue',
        text: 'Show my investigation queue',
        description: 'View pending anomalies',
        icon: '📋',
      },
    ],
  },

  scenarios: {
    name: 'Scenario Planning',
    primaryColor: '#06B6D4',
    secondaryColor: '#0891B2',
    keywords: ['scenario', 'simulation', 'monte carlo', 'risk', 'var', 'probability'],
    queries: [
      {
        label: 'Run Monte Carlo Simulation',
        text: 'Run Monte Carlo simulation with 10,000 scenarios',
        description: 'Stochastic optimization',
        icon: '🎲',
      },
      {
        label: 'Risk Metrics',
        text: 'Calculate VaR and CVaR metrics',
        description: 'View downside risk measures',
        icon: '📉',
      },
      {
        label: 'Best/Worst Case',
        text: 'Show best and worst case scenarios',
        description: 'View outcome ranges',
        icon: '📊',
      },
      {
        label: 'Probability Distribution',
        text: 'Show probability distribution of outcomes',
        description: 'View likelihood of results',
        icon: '📈',
      },
      {
        label: 'Stress Test',
        text: 'Run stress test scenarios',
        description: 'Test extreme conditions',
        icon: '⚠️',
      },
    ],
  },

  tax: {
    name: 'Tax Optimization',
    primaryColor: '#10B981',
    secondaryColor: '#059669',
    keywords: ['tax', 'etr', 'beps', 'transfer pricing', 'jurisdiction'],
    queries: [
      {
        label: 'Optimize Tax Structure',
        text: 'Optimize global tax structure to minimize ETR',
        description: 'Run MINLP optimization',
        icon: '🌍',
      },
      {
        label: 'Transfer Pricing',
        text: 'Optimize transfer pricing allocation',
        description: 'Maximize tax efficiency',
        icon: '💱',
      },
      {
        label: 'BEPS Compliance',
        text: 'Check BEPS compliance status',
        description: 'Verify regulatory requirements',
        icon: '✅',
      },
      {
        label: 'ETR Calculation',
        text: 'Calculate effective tax rate',
        description: 'View current and optimized ETR',
        icon: '📊',
      },
      {
        label: 'Tax Savings',
        text: 'What are potential tax savings?',
        description: 'Estimate optimization benefits',
        icon: '💰',
      },
    ],
  },

  competitive: {
    name: 'Competitive Intelligence',
    primaryColor: '#6366F1',
    secondaryColor: '#4F46E5',
    keywords: ['competitive', 'market share', 'pricing', 'competitor', 'strategy'],
    queries: [
      {
        label: 'Market Analysis',
        text: 'Analyze current market position and share',
        description: 'View competitive landscape',
        icon: '🎯',
      },
      {
        label: 'Pricing Strategy',
        text: 'Optimize pricing strategy vs competitors',
        description: 'Find optimal price points',
        icon: '💰',
      },
      {
        label: 'Competitor Tracking',
        text: 'Show competitor performance metrics',
        description: 'View competitor data',
        icon: '👁️',
      },
      {
        label: 'Market Share Trends',
        text: 'What are market share trends?',
        description: 'View historical trends',
        icon: '📈',
      },
      {
        label: 'Strategic Positioning',
        text: 'Assess strategic positioning',
        description: 'Get positioning recommendations',
        icon: '🧭',
      },
    ],
  },
};

/**
 * Get mascot configuration for a specific module
 */
export const getMascotConfig = (module: string): MascotConfig => {
  const config = moduleConfigs[module.toLowerCase()];

  if (!config) {
    console.warn(`No mascot config found for module: ${module}, using dashboard config`);
    return moduleConfigs.dashboard;
  }

  return config;
};

/**
 * Get all available modules
 */
export const getAllModules = (): string[] => {
  return Object.keys(moduleConfigs);
};

/**
 * Search queries across all modules
 */
export const searchQueries = (searchTerm: string): Array<PreCannedQuery & { module: string }> => {
  const results: Array<PreCannedQuery & { module: string }> = [];
  const lowerSearch = searchTerm.toLowerCase();

  Object.entries(moduleConfigs).forEach(([module, config]) => {
    config.queries.forEach((query) => {
      if (
        query.label.toLowerCase().includes(lowerSearch) ||
        query.text.toLowerCase().includes(lowerSearch) ||
        query.description.toLowerCase().includes(lowerSearch)
      ) {
        results.push({ ...query, module });
      }
    });
  });

  return results;
};
